import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@bastion/data';
import type { ChatMessage } from '../types';

export interface Conversation {
  id: string;
  session_id: string;
  title: string | null;
  summary: string | null;
  created_at: string;
  updated_at: string;
}

interface ConversationDetail {
  session_id: string;
  messages: ChatMessage[];
}

export function useConversations(limit = 50) {
  return useQuery<Conversation[]>({
    queryKey: ['conversations', limit],
    queryFn: () => authenticatedFetch<Conversation[]>(`/conversations?limit=${limit}`),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useConversationMessages(sessionId: string | undefined) {
  return useQuery<ConversationDetail>({
    queryKey: ['conversation', sessionId],
    queryFn: () => authenticatedFetch<ConversationDetail>(`/conversations/${sessionId}`),
    enabled: !!sessionId,
    staleTime: 60_000,
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      authenticatedFetch<{ ok: boolean }>(`/conversations/${sessionId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, sessionId) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation', sessionId] });
    },
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      authenticatedFetch<{ ok: boolean }>(`/conversations/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({
        queryKey: ['conversation', variables.sessionId],
      });
    },
  });
}
